import React from 'react';
import styled from 'styled-components';
import { Card, Group, LinkHeading, AddressTag } from './StyledAddressCard';
import { Text, withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import { withEditMode, withEditModeNotice } from '_containers/BaseComponent';
import { compose, map as Rmap, pick } from 'ramda';
import MapStatic from '../MapStatic';
import NavLink from '../NavItem';

import GetDirectionsLink from '../GetDirectionsLink';

import { addressClean, addressMerge } from '_utils/data';
import { normalizeJssFields } from '_utils/helpers/jss';
import { getLink } from '_utils/helpers/link';
import PropTypes from 'prop-types';

type AddressCardProps = {
  sitecoreContext: any;
  rendering: any;
  editMode: boolean;
};

const addressFields = ['addressLine1', 'addressLine2', 'addressLine3', 'suburb', 'postcode'];

// functor to retrieve value
const value = (x) => x.value;

const filteredFieldValues = (fields, keys) => {
  if (!fields) {
    return {};
  }

  if (!keys) {
    return Rmap(value, fields);
  }

  return Rmap(value, pick(keys, fields));
};

const EditorText = compose(withEditModeNotice)(Text);
const MissingDataSource = compose(withEditModeNotice)(styled.div``);

const Address = ({ editMode, fields }) => {
  let primaryAddress = filteredFieldValues(fields, addressFields);
  let relatedAddress = filteredFieldValues(fields.place?.fields, addressFields);

  if (fields.space && fields.space.fields.place) {
    relatedAddress = filteredFieldValues(fields.space.fields.place.fields, addressFields);
    const cleanedAddress = addressClean(primaryAddress);
    if (Object.keys(cleanedAddress).length === 0) {
      primaryAddress = filteredFieldValues(fields.space.fields, addressFields);
    }
  }
  else if (fields.space) {
    relatedAddress = filteredFieldValues(fields.space.fields, addressFields);
  }

  if (editMode) {
    return (
      <Group>
        {addressFields.map((key) => (
          <EditorText
            tag="p"
            key={key}
            field={fields[key]}
            noticeEnabled={relatedAddress && relatedAddress[key]}
            noticeLevel="info"
            noticeMessage={`Overrides (${relatedAddress[key]})`}
          />
        ))}
      </Group>
    );
  } else {
    const mergedAddress = addressClean(addressMerge(primaryAddress, relatedAddress));

    const { addressLine1, addressLine2, addressLine3, postcode, suburb } = mergedAddress || {};

    let placeName = '';
    let placeUrl = '';
    
    if (fields.space && fields.space.fields.place) {
      placeName = fields.space.fields.place.fields.pageTitle.value;
      placeUrl = fields.space.fields.place.url;
    }
    else if (fields.space) {
      placeName = fields.space.fields.pageTitle.value;
      placeUrl = fields.space.url;
    }

    if (placeName === '' && fields.place) {
      placeName = fields.place.fields.pageTitle.value;
      placeUrl = fields.place.url;
    }

    if (!addressLine1 && !addressLine2 && !addressLine3 && !postcode && !suburb) {
      return null;
    }

    const addressLine4 = suburb || postcode ? [suburb, postcode].join(' ') : '';

    const link = getLink(placeUrl);

    return (
      <Group>
        <AddressTag>
          {placeName && (
            <NavLink link={link} StyledLink={LinkHeading}>
              {placeName}
            </NavLink>
          )}
          {addressLine1 && <div>{addressLine1}</div>}
          {addressLine2 && <div>{addressLine2}</div>}
          {addressLine3 && <div>{addressLine3}</div>}
          {addressLine4 && <div>{addressLine4}</div>}

          <GetDirectionsLink fields={fields} />
        </AddressTag>
      </Group>
    );
  }
};

Address.propTypes = {
  editMode: PropTypes.bool,
  fields: PropTypes.shape({
    addressLine1: PropTypes.object,
    addressLine2: PropTypes.object,
    addressLine3: PropTypes.object,
    suburb: PropTypes.object,
    postcode: PropTypes.object,
    place: PropTypes.shape({
      fields: PropTypes.shape({
        addressLine1: PropTypes.object,
        addressLine2: PropTypes.object,
        addressLine3: PropTypes.object,
        suburb: PropTypes.object,
        postcode: PropTypes.object,
        pageTitle: PropTypes.shape({
          value: PropTypes.string
        }),
        url: PropTypes.string
      }),
      url: PropTypes.string
    }),
    space: PropTypes.shape({
      fields: PropTypes.shape({
        addressLine1: PropTypes.object,
        addressLine2: PropTypes.object,
        addressLine3: PropTypes.object,
        suburb: PropTypes.object,
        postcode: PropTypes.object,
        place: PropTypes.shape({
          fields: PropTypes.shape({
            addressLine1: PropTypes.object,
            addressLine2: PropTypes.object,
            addressLine3: PropTypes.object,
            suburb: PropTypes.object,
            postcode: PropTypes.object,
            pageTitle: PropTypes.shape({
              value: PropTypes.string
            }),
            url: PropTypes.string
          }),
          url: PropTypes.string
        }),
        pageTitle: PropTypes.shape({
          value: PropTypes.string
        }),
        url: PropTypes.string
      }),
      url: PropTypes.string
    }),
    url: PropTypes.string
  })
};

const AddressCard: React.FC<AddressCardProps> = ({ sitecoreContext, rendering, editMode }) => {

  const googleMapsApiKey = rendering?.fields?.data?.apiKey?.googleMapsApiKey;
  const dataSource = rendering?.fields?.data?.dataSource;

  if (!dataSource) {

    const route = sitecoreContext?.route;
    let { fields } = route || {};
  
    const relatedFields = fields?.place?.fields || {};

    if (
      editMode &&
      !fields?.addressLine1 &&
      !fields?.addressLine2 &&
      !fields?.addressLine3 &&
      !fields?.suburb &&
      !fields?.postcode) {
      return (
        <MissingDataSource noticeEnabled={true} noticeMessage={`Address card has no data source`} />
      );
    }
 
    if (
      !editMode &&
      !fields?.addressLine1?.value &&
      !fields?.addressLine2?.value &&
      !fields?.addressLine3?.value &&
      !fields?.suburb?.value &&
      !fields?.postcode?.value &&
      !relatedFields?.addressLine1?.value &&
      !relatedFields?.addressLine2?.value &&
      !relatedFields?.addressLine3?.value &&
      !relatedFields?.suburb?.value &&
      !relatedFields?.postcode?.value
    ) {
      return null;
    } else {
      return (
        <Card>
          <MapStatic fields={fields} apiKey={googleMapsApiKey} editMode={editMode} />
          <Address fields={fields} editMode={editMode}></Address>
        </Card>
      );
    }

  } else {

    const fields = normalizeJssFields(dataSource);
    
    if (
      !editMode &&
      !fields?.addressLine1?.value &&
      !fields?.addressLine2?.value &&
      !fields?.addressLine3?.value &&
      !fields?.suburb?.value &&
      !fields?.postcode?.value
    ) {
      return null;
    } else {
      return (
        <Card>
          <MapStatic fields={fields} apiKey={googleMapsApiKey} editMode={editMode} />
          <Address fields={fields} editMode={editMode}></Address>
        </Card>
      );
    }
  }
};

AddressCard.propTypes = {
  sitecoreContext: PropTypes.any,
  rendering: PropTypes.shape({
    fields: PropTypes.shape({
      data: PropTypes.shape({
        apiKey: PropTypes.shape({
          googleMapsApiKey: PropTypes.shape({
              value: PropTypes.string
            }),
        }),
        dataSource: PropTypes.any
      })
    })
  }),
  editMode: PropTypes.bool
};

export default compose(withSitecoreContext(), withEditMode)(AddressCard);